import client from '../../config/api';
import {
  LOCAL_SET_CURRENT_USER,
  LOCAL_SET_USERS,
  LOCAL_SET_USERS_PENDING_APPROVAL,
  LOCAL_SET_USERS_LOGGED_IN_TODAY,
  LOCAL_SET_USERS_REJECTED,
  LOCAL_SET_USERS_WITHOUT_SEPIA_INFO,
} from './mutations/mutationTypes';

let usersProgressCount = 0;
let usersProgressTotal = 0;
let usersProgressCompleted = 0;
const usersProgressLabels = new Set();

function updateUsersProgressMessage(commit) {
  const labels = Array.from(usersProgressLabels);
  const suffix = labels.length ? ` (${labels.join(', ')})` : '';
  const progressValue =
    usersProgressTotal > 0
      ? Math.min(
          100,
          20 + Math.round((usersProgressCompleted / usersProgressTotal) * 80)
        )
      : 20;
  commit(
    'SET_PROGRESS_BAR',
    { value: progressValue, message: `Loading users${suffix}...` },
    { root: true }
  );
}

function startUsersProgress(commit, label) {
  if (usersProgressCount === 0) {
    usersProgressTotal = 0;
    usersProgressCompleted = 0;
  }
  usersProgressCount += 1;
  usersProgressTotal += 1;
  if (label) {
    usersProgressLabels.add(label);
  }
  updateUsersProgressMessage(commit);
}

function endUsersProgress(commit, label) {
  usersProgressCount = Math.max(0, usersProgressCount - 1);
  usersProgressCompleted = Math.min(
    usersProgressTotal,
    usersProgressCompleted + 1
  );
  if (label) {
    usersProgressLabels.delete(label);
  }
  if (usersProgressCount === 0) {
    commit('SET_PROGRESS_BAR', { value: null, message: '' }, { root: true });
    usersProgressTotal = 0;
    usersProgressCompleted = 0;
    usersProgressLabels.clear();
  } else {
    updateUsersProgressMessage(commit);
  }
}

function handleError(error) {
  throw error;
}

export default {
  async USERS_GET_CURRENT({ commit }) {
    try {
      const result = await client.get('/users/current');
      const user = result?.data;
      if (
        !user ||
        typeof user !== 'object' ||
        Array.isArray(user) ||
        !user._id
      ) {
        const invalidCurrentUserError = new Error('Current user not found');
        invalidCurrentUserError.status = 404;
        throw invalidCurrentUserError;
      }
      commit(LOCAL_SET_CURRENT_USER, user);
      commit('SET_CURRENT_USER', user, { root: true }); // We still have the current user not refactored on root state
      return user;
    } catch (error) {
      console.warn(error);
      handleError(error);
    }
  },

  /**
   * Get the list of users to show on admin's page
   */
  async GET_USERS({ commit }, query) {
    startUsersProgress(commit, 'Approved');
    try {
      const { data } = await client.get('/users', {
        params: {
          ...query,
        },
      });
      commit(LOCAL_SET_USERS, data);
      return data;
    } finally {
      endUsersProgress(commit, 'Approved');
    }
  },

  /**
   * Get the list of users pending approval
   */
  async GET_USERS_PENDING_APPROVAL({ commit }, query) {
    startUsersProgress(commit, 'Pending Approval');
    const queryWithPendingApproval = { pendingApproval: true, ...query };
    try {
      const result = await client.get('/users', {
        params: {
          ...queryWithPendingApproval,
        },
      });
      commit(LOCAL_SET_USERS_PENDING_APPROVAL, result.data);
    } finally {
      endUsersProgress(commit, 'Pending Approval');
    }
  },

  async GET_USERS_LOGGED_IN_TODAY({ commit }, query) {
    // eslint-disable-next-line
    startUsersProgress(commit, "Today's Logins");
    const now = new Date();
    const lastMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const queryWithLoggedInToday = {
      loggedInAfter: lastMidnight.getTime(),
      ...query,
    };
    try {
      const result = await client.get('/users', {
        params: {
          ...queryWithLoggedInToday,
        },
      });
      commit(LOCAL_SET_USERS_LOGGED_IN_TODAY, result.data);
    } finally {
      // eslint-disable-next-line
      endUsersProgress(commit, "Today's Logins");
    }
  },

  async GET_USERS_REJECTED({ commit }, query) {
    startUsersProgress(commit, 'Rejected');
    const queryWithRejected = { rejected: true, ...query };
    try {
      const result = await client.get('/users', {
        params: {
          ...queryWithRejected,
        },
      });
      commit(LOCAL_SET_USERS_REJECTED, result.data);
    } finally {
      endUsersProgress(commit, 'Rejected');
    }
  },

  async GET_USERS_WITHOUT_SEPIA_INFO({ commit }, query) {
    startUsersProgress(commit, 'Inactive');
    const queryWithNoSepiaInfo = { noSepiaInfo: true, ...query };
    try {
      const result = await client.get('/users', {
        params: {
          ...queryWithNoSepiaInfo,
        },
      });
      commit(LOCAL_SET_USERS_WITHOUT_SEPIA_INFO, result.data);
    } finally {
      endUsersProgress(commit, 'Inactive');
    }
  },
};
